import React from "react"

import { resolveCustomTraceUnit } from "@/metrics/registry/configuration/utils/traceConstants"
import {
  compareTraceOutputs,
  isTraceValuesOutput,
  normalizeTraceOutput,
} from "@/metrics/runtime/record-data"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { CustomTraceConfig } from "../../configuration/components/CustomTraceConfig"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import {
  traceComparedOverview,
  traceInDepthReview,
  traceOverview,
} from "../helpers/CustomTraceOutput"
import {
  CustomTraceValues,
  CustomTraceValuesSummary,
} from "../helpers/CustomTraceValues"

export function renderCustomTraceEditorMetadata(
  metricRecordData: MetricRecordData,
  onChange: (metric: MetricRecordData) => void,
  _textAreaRef?: React.RefObject<HTMLTextAreaElement>,
  editorContext?: Parameters<NumericalMetricRecord["renderEditorMetadata"]>[3],
): React.ReactNode {
  return (
    <CustomTraceConfig
      metricRecordData={metricRecordData}
      onChange={onChange}
      selectedAgentIds={editorContext?.selectedAgentIds}
      supportsValues={Boolean(
        editorContext?.metricDirectoryEntry?.candidate_output_types?.includes(
          "set",
        ),
      )}
    />
  )
}
export function renderCustomTraceMetadataField(
  metricRecordData: MetricRecordData,
): React.ReactNode {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <h3 className="font-semibold">Span Name: </h3>
        <p>{metricRecordData.metric_metadata?.span_name}</p>
      </div>
      <div>
        <h3 className="font-semibold">Metric Attribute: </h3>
        <p>
          {metricRecordData.metric_metadata?.value_source === "duration"
            ? "Span duration"
            : metricRecordData.metric_metadata?.metric_attribute}
        </p>
      </div>
      <div>
        <h3 className="font-semibold">Aggregation Method: </h3>
        <p>{metricRecordData.metric_metadata?.aggregation_method}</p>
      </div>
      <div>
        <h3 className="font-semibold">Unit: </h3>
        <p>{resolveCustomTraceUnit(metricRecordData.metric_metadata ?? {})}</p>
      </div>
    </div>
  )
}

export class CustomTraceMetricRecord extends NumericalMetricRecord {
  private readonly sourceData: MetricRecordData

  constructor(data: MetricRecordData) {
    super(data)
    this.sourceData = data
  }
  renderEditorMetadata = renderCustomTraceEditorMetadata
  renderMetadataField = renderCustomTraceMetadataField

  renderMetricType(): React.ReactNode {
    return "Custom Trace Metric"
  }

  renderBadge(
    ...[output, rerun]: Parameters<NumericalMetricRecord["renderBadge"]>
  ): React.ReactNode {
    return super.renderBadge(normalizeTraceOutput(output), rerun)
  }

  compareValues(...values: Parameters<typeof compareTraceOutputs>): boolean {
    return compareTraceOutputs(...values)
  }

  getComparedOverviewNode(
    ...[runRows, compareBy]: Parameters<
      NumericalMetricRecord["getComparedOverviewNode"]
    >
  ): React.ReactNode {
    return traceComparedOverview(
      this.sourceData,
      this._getMetricOutputsFromRunRows(runRows),
      runRows,
      compareBy,
    )
  }

  renderInDepthRunReview(
    ...[outputs, context]: Parameters<
      NumericalMetricRecord["renderInDepthRunReview"]
    >
  ): React.ReactNode {
    return traceInDepthReview(this.sourceData, outputs, context)
  }

  renderComparedInDepthRunReview(
    ...[outputs, runRows, compareBy]: Parameters<
      NumericalMetricRecord["renderComparedInDepthRunReview"]
    >
  ): React.ReactNode {
    return traceComparedOverview(this.sourceData, outputs, runRows, compareBy)
  }

  hasValidOutputValue(mo: RunMetricOutput): boolean {
    return isTraceValuesOutput(mo)
      ? Array.isArray(mo.set_value)
      : super.hasValidOutputValue(mo)
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    return isTraceValuesOutput(mo) ? (
      <>
        <span className="text-coval-text-muted">Previous Values result: </span>
        <CustomTraceValuesSummary mo={mo} />
      </>
    ) : (
      super._renderSimulationCard(mo)
    )
  }

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode {
    return isTraceValuesOutput(mo) ? (
      <>
        <p className="text-sm text-coval-text-muted">
          Values result from an earlier metric configuration.
        </p>
        <CustomTraceValues mo={mo} />
      </>
    ) : (
      super.getSingleMetricOutputNode(mo)
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    return this.getSingleMetricOutputNode(mo)
  }

  getOverviewNode(
    mos: RunMetricOutput[],
    options?: Parameters<NumericalMetricRecord["getOverviewNode"]>[1],
  ): React.ReactNode {
    if (mos.every((output) => !isTraceValuesOutput(output))) {
      return super.getOverviewNode(mos, options)
    }
    return traceOverview(this.sourceData, mos, options)
  }
}
